import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  createStudentProject,
  requisitionQueryKeys,
  updateStudentProject,
} from '../api/productRequisition.api';
import type { StudentProject } from '../types/productRequisition.types';

interface ProjectFormProps {
  project: StudentProject | null;
  onDone: () => void;
  onCancel: () => void;
}

function ProjectForm({ project, onDone, onCancel }: ProjectFormProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(() => project?.name ?? '');
  const [code, setCode] = useState(() => project?.code ?? '');
  const [startDate, setStartDate] = useState(() => project?.startDate ?? '');
  const [deadlineDate, setDeadlineDate] = useState(() => project?.deadlineDate ?? '');
  const [comments, setComments] = useState(() => project?.comments ?? '');
  const [isActive, setIsActive] = useState(() => project?.isActive ?? true);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        ...(code.trim() === '' ? {} : { code: code.trim() }),
        ...(startDate === '' ? {} : { startDate }),
        ...(deadlineDate === '' ? {} : { deadlineDate }),
        ...(comments.trim() === '' ? {} : { comments: comments.trim() }),
        isActive,
      };

      return project === null
        ? createStudentProject(payload)
        : updateStudentProject(project.id, payload);
    },
    onSuccess: () => {
      toast.success(project === null ? 'Project opened' : 'Project saved');
      void queryClient.invalidateQueries({ queryKey: requisitionQueryKeys.all() });
      onDone();
    },
    onError: () => toast.error('Could not save the project'),
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{project === null ? 'Open a Project' : 'Edit Project'}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="project-name">Project Name</Label>
          <Input id="project-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="project-code">Project Code</Label>
          <Input id="project-code" value={code} onChange={(event) => setCode(event.target.value)} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="project-start">Start Date</Label>
            <DatePickerInput id="project-start" value={startDate} onChange={setStartDate} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="project-deadline">Deadline Date</Label>
            <DatePickerInput
              id="project-deadline"
              value={deadlineDate}
              onChange={setDeadlineDate}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="project-comments">Comments</Label>
          <Textarea
            id="project-comments"
            value={comments}
            onChange={(event) => setComments(event.target.value)}
          />
        </div>

        <div className="flex items-center gap-3">
          <Switch id="project-active" checked={isActive} onCheckedChange={setIsActive} />
          <Label htmlFor="project-active">Active</Label>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || name.trim() === ''}
        >
          Save
        </Button>
      </DialogFooter>
    </>
  );
}

interface StudentProjectFormModalProps {
  project: StudentProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StudentProjectFormModal({
  project,
  open,
  onOpenChange,
}: StudentProjectFormModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {open ? (
          <ProjectForm
            key={project?.id ?? 'new'}
            project={project}
            onDone={() => onOpenChange(false)}
            onCancel={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
